import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import {
  Modal,
  Tag,
  Button,
  Table,
  Avatar,
  Spin,
  message,
  Typography,
  Checkbox,
  Dropdown,
} from "antd";
import {
  FilePdfOutlined,
  UserOutlined,
  HistoryOutlined,
  FilterOutlined,
  CloseCircleFilled,
  CheckCircleFilled,
  ToolOutlined,
  TagOutlined,
  PlusCircleFilled,
  WarningFilled,
} from "@ant-design/icons";
import { CALL_API } from "common/API";
import { STATUS_CODE } from "common/Constants";
import ViewDocument from "reusableComponent/Popup/viewDocument";
import download from "downloadjs";
import { useSelector } from "react-redux";
import {
  getAllDepartmentList,
  getMenuList,
  getUserInfo,
  getUserList,
  getUserRoleId,
} from "redux/selectors";
import { startCase } from "lodash";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { ReactComponent as AddedIcon } from "assets/added.svg";
import { ReactComponent as TicketRiseIcon } from "assets/ticketrise.svg";
import { ReactComponent as SentServiceIcon } from "assets/sentservice.svg";
import { ReactComponent as ServiceCompleteIcon } from "assets/servicecomplete.svg";
import { ReactComponent as AssignIcon } from "assets/assign.svg";
import { ReactComponent as RevokeIcon } from "assets/revoke.svg";

const { Text } = Typography;

/* ─── Styled ─────────────────────────────────────────────── */
const ModalBody = styled.div`
  padding: 0;
`;
const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
`;
const HeaderLeft = styled.div``;
const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
`;
const AssetTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #1a1a1a;
`;
const AssetSubtitle = styled.div`
  font-size: 13px;
  color: #888;
`;
const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;
const InServiceTag = styled(Tag)`
  background: #fff3e0;
  color: #e65100;
  border-color: #ffb74d;
  border-radius: 12px;
  padding: 3px 12px;
  font-size: 12px;
`;
const Section = styled.div`
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  padding: 16px 20px;
  margin-bottom: 16px;
`;
const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #555;
  margin-bottom: 14px;
`;
const OwnerCard = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const OwnerLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;
const OwnerInfo = styled.div``;
const OwnerName = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #1a1a1a;
`;
const OwnerMeta = styled.div`
  font-size: 12px;
  color: #888;
  margin-top: 2px;
`;
const OwnerRight = styled.div`
  text-align: right;
`;
const AssignedLabel = styled.div`
  font-size: 12px;
  color: #888;
`;
const AssignedDate = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #1a1a1a;
  margin-top: 2px;
`;
const OtherInfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
  align-items: start;
`;
const InfoBlock = styled.div``;
const InfoLabel = styled.div`
  font-size: 12px;
  color: #888;
  margin-bottom: 4px;
`;
const InfoValue = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #1a1a1a;
`;
const AttachmentLink = styled.a`
  color: #2563eb;
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;
const HistoricalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;
const ServiceCount = styled.span`
  color: ${({ $count }) => ($count > 0 ? "#ef4444" : "#888")};
  font-weight: 600;
`;
const TimelineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;
const TimelineSectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #555;
`;
const FilterBtn = styled(Button)`
  border-radius: 8px;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
`;
const TimelineList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;
const TimelineItem = styled.div`
  display: flex;
  gap: 14px;
  position: relative;
  padding-bottom: 20px;
  &:last-child {
    padding-bottom: 0;
  }
  &:last-child .tl-line {
    display: none;
  }
`;
const TLIconWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  padding-top: 2px;
`;
const TLLine = styled.div`
  width: 2px;
  flex: 1;
  background: #e8e8e8;
  margin-top: 6px;
  min-height: 24px;
`;
const TLContent = styled.div`
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;
const TLLeft = styled.div`
  flex: 1;
`;
const TLEventName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 3px;
`;
const TLEventDesc = styled.div`
  font-size: 12px;
  color: #666;
  line-height: 1.5;
`;
const TLDate = styled.div`
  font-size: 12px;
  color: #aaa;
  flex-shrink: 0;
  margin-left: 12px;
`;
const FilterMenuWrap = styled.div`
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  padding: 10px 0;
  min-width: 200px;
`;
const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  cursor: pointer;
  &:hover {
    background: #f8f8f8;
  }
  font-size: 13px;
  color: #333;
`;
const FilterTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 16px 10px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 4px;
`;
const ResetBtn = styled.span`
  font-size: 12px;
  color: #2563eb;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

/* ─── Constants ──────────────────────────────────────────── */
const EVENT_TYPES = [
  "Asset Revoked",
  "Sent to Service",
  "Ticket Raised",
  "Asset Assignment",
  "Asset Added",
  "Service Completed",
];

const eventConfig = {
  "Asset Revoked": {
    color: "#6b7280",
    Icon: RevokeIcon,
  },
  "Service Completed": {
    color: "#22c55e",
    Icon: ServiceCompleteIcon,
  },
  "Sent to Service": {
    color: "#3b82f6",
    Icon: SentServiceIcon,
  },
  "Ticket Raised": {
    color: "#ef4444",
    Icon: TicketRiseIcon,
  },
  "Asset Assignment": {
    color: "#8b5cf6",
    Icon: AssignIcon,
  },
  "Asset Added": {
    color: "#f59e0b",
    Icon: AddedIcon,
  },
};

const formatDate = (iso) => {
  if (!iso) return "---";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const buildHistoricalOwners = (detailArray = []) =>
  detailArray.map((item, idx) => ({
    key: idx,
    ownername: item?.assigned_to || "—",
    role: "—",
    avatar: null,
    period: item.assigned_on
      ? `${formatDate(item.assigned_on)} – ${
          item.revoked_on ? formatDate(item.revoked_on) : "Present"
        }`
      : "—",
    servicecount: item.senttoservice ? 1 : 0,
  }));

const buildTimeline = (asset, detailArray = [], findUser) => {
  const events = [];
  detailArray.forEach((item) => {
    if (item.assets_added)
      events.push({
        type: "Asset Added",
        desc: `Under ${asset?.categoryname} > ${asset?.categorytype} > ${asset?.model}.`,
        date: item.assets_added,
      });
    if (item.assigned_on)
      events.push({
        type: "Asset Assignment",
        desc: `Assigned to ${findUser(item.assigned_to)?.fullname || "user"}.`,
        date: item.assigned_on,
      });
    if (item.senttoservice)
      events.push({
        type: "Sent to Service",
        desc: `Sent to ${item?.service_provider}.`,
        date: item.senttoservice,
      });
    if (item.servicecompleted)
      events.push({
        type: "Service Completed",
        desc: `${item?.repair_details}.`,
        date: item.servicecompleted,
      });
    if (item.revoked_on)
      events.push({
        type: "Asset Revoked",
        desc: `Revoked from ${findUser(item.assigned_to)?.fullname || "user"}.`,
        date: item.revoked_on,
      });
  });
  events.sort((a, b) => new Date(b.date) - new Date(a.date));
  return events;
};

const FilterMenu = ({ selected, onChange, onReset }) => (
  <FilterMenuWrap>
    <FilterTopRow>
      <Checkbox checked={selected.length === 0} onChange={onReset}>
        <span style={{ fontSize: 13 }}>All</span>
      </Checkbox>
      <ResetBtn onClick={onReset}>Reset</ResetBtn>
    </FilterTopRow>
    {EVENT_TYPES.map((type) => (
      <FilterRow key={type} onClick={() => onChange(type)}>
        <Checkbox checked={selected.includes(type)} />
        <span>{type}</span>
      </FilterRow>
    ))}
  </FilterMenuWrap>
);

/* ─── Component ──────────────────────────────────────────── */
const AssetDetailDrawer = ({
  open,
  onClose,
  asset,
  onSuccess,
  onEdit,
  getAssets,
  fetchMasterData,
}) => {
  const menuList = useSelector(getMenuList);
  const userRoleId = useSelector(getUserRoleId);

  const Menulist =
    menuList[userRoleId] || Object.values(menuList)[0] || menuList;
  const userList = useSelector(getUserList);
  const userInfo = useSelector(getUserInfo);
  const departmentList = useSelector(getAllDepartmentList);
  const [isPdfExporting, setIsPdfExporting] = useState(false);

  /* ref for PDF capture — wraps entire modal body */
  const contentRef = useRef(null);

  const [detail, setDetail] = useState([]);
  const [loading, setLoading] = useState(false);
  const [revokeLoading, setRevokeLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [preview, setPreview] = useState(false);
  const [previewURL, setPreviewURL] = useState();

  const ownerUser = userList?.find((u) => u.userid === asset?.assigned_to);
  const findUser = (userid) => userList?.find((u) => u.userid === userid) || {};

  /* ── Attachment preview ── */
  const handlePreviewClick = async (data) => {
    try {
      setLoading(true);
      const ext = data?.filename?.split(".")?.pop();
      const { code, url } = await CALL_API(
        `assets/assets-file/${data?.assetid}`,
        "get",
        {}
      );
      if (code === STATUS_CODE.SUCCESS) {
        if (["jpg", "jpeg", "png", "svg", "pdf"].includes(ext)) {
          setPreviewURL(url);
          setPreview(true);
        } else {
          download(url);
        }
      } else {
        message.error("Preview failed. Try again.");
      }
    } catch {
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  /* ── Fetch timeline ── */
  const getAssetDetail = async () => {
    if (!asset?.assetid) return;
    try {
      setLoading(true);
      const response = await CALL_API(
        `assets/assets-performance-timeline/${asset.assetid}`,
        "get"
      );
      if (response.code === STATUS_CODE.SUCCESS) {
        const list = Array.isArray(response.assetList)
          ? response.assetList
          : Array.isArray(response)
          ? response
          : [];
        setDetail(list);
      } else {
        throw new Error(`Failed: ${response.code}`);
      }
    } catch (error) {
      message.error("Failed to load asset details");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && asset) {
      setSelectedFilters([]);
      getAssetDetail();
    }
  }, [open, asset]);

  /* ── Revoke ── */
  const handleRevokeAsset = async (record) => {
    try {
      setRevokeLoading(true);
      const { code } = await CALL_API(
        `/assets/revokeassets/${record.assetid}`,
        "patch",
        {
          revoked_by: userInfo?.userid,
          revoked_on: new Date(),
          assetstatus: "Available",
        }
      );
      if (code === STATUS_CODE.SUCCESS) {
        await fetchMasterData(true);
        message.success("Asset revoked successfully");
        await getAssets?.();
        onClose();
      } else {
        message.error("Failed to revoke asset");
      }
    } catch (error) {
      console.error(error);
      message.error("Failed to revoke asset");
    } finally {
      setRevokeLoading(false);
    }
  };

  const confirmRevokeAsset = () => {
    Modal.confirm({
      title: "Revoke Asset",
      content: `Are you sure you want to revoke ${asset?.assetname}?`,
      okText: "Revoke",
      cancelText: "Cancel",
      centered: true,
      okButtonProps: { loading: revokeLoading },
      onOk: () => handleRevokeAsset(asset),
    });
  };

  /* ── Export PDF — client-side html2canvas + jsPDF ── */
  const handleExportPDF = async () => {
    if (!contentRef.current) return;

    try {
      setExportLoading(true);
      setIsPdfExporting(true);

      // wait for UI update
      await new Promise((resolve) => setTimeout(resolve, 200));

      const element = contentRef.current;

      const modalBody = element.closest(".ant-modal-body");
      const prevMaxHeight = modalBody?.style.maxHeight;
      const prevOverflow = modalBody?.style.overflowY;

      if (modalBody) {
        modalBody.style.maxHeight = "none";
        modalBody.style.overflowY = "visible";
      }

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
      });

      if (modalBody) {
        modalBody.style.maxHeight = prevMaxHeight;
        modalBody.style.overflowY = prevOverflow;
      }

      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4");

      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 10;

      const usableW = pageW - margin * 2;
      const usableH = pageH - margin * 2;

      const imgW = canvas.width;
      const imgH = canvas.height;

      const mmPerPx = usableW / imgW;
      const totalMmH = imgH * mmPerPx;

      let yMm = 0;
      let page = 0;

      while (yMm < totalMmH) {
        if (page > 0) pdf.addPage();

        const srcY = yMm / mmPerPx;
        const sliceH = Math.min(usableH / mmPerPx, imgH - srcY);

        const slice = document.createElement("canvas");

        slice.width = imgW;
        slice.height = sliceH;

        slice
          .getContext("2d")
          .drawImage(canvas, 0, srcY, imgW, sliceH, 0, 0, imgW, sliceH);

        pdf.addImage(
          slice.toDataURL("image/png"),
          "PNG",
          margin,
          margin,
          usableW,
          sliceH * mmPerPx
        );

        yMm += usableH;
        page++;
      }

      pdf.save(`${asset?.assetname || asset?.assetid || "asset"}_detail.pdf`);
    } catch (err) {
      console.error(err);
      message.error("Failed to export PDF");
    } finally {
      setIsPdfExporting(false);
      setExportLoading(false);
    }
  };

  const handleClose = () => {
    setDetail([]);
    onClose();
  };

  const toggleFilter = (type) =>
    setSelectedFilters((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  const resetFilters = () => setSelectedFilters([]);

  /* ── Derived data ── */
  const purchaseDate = formatDate(asset?.purchasedon);
  const warrantyEnd = formatDate(asset?.warrantyends);
  const assignedOn = formatDate(asset?.assigned_on);

  const currentOwner = asset?.assigned_to
    ? {
        name: ownerUser?.fullname,
        role: ownerUser?.jobtitle || "-",
        department: departmentList?.find(
          (d) => d.department_id === ownerUser?.basedepartmentid
        )?.department_name,
        avatar: ownerUser?.profileurl || "",
        assignedon: assignedOn,
      }
    : null;

  const historicalOwners = buildHistoricalOwners(detail);
  console.log("historicalOwners", historicalOwners);
  const allTimelineEvents = buildTimeline(asset, detail, findUser);
  const timelineEvents =
    selectedFilters.length === 0
      ? allTimelineEvents
      : allTimelineEvents.filter((e) => selectedFilters.includes(e.type));

  const historyColumns = [
    {
      title: "Owner",
      key: "owner",
      render: (_, r) => {
        const user = findUser(r.ownername);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {user?.profileurl ? (
              <Avatar src={user.profileurl} size={48} />
            ) : (
              <Avatar>{startCase(user.fullname || "?").charAt(0)}</Avatar>
            )}
            <div>
              <div style={{ fontWeight: 500, fontSize: 13 }}>
                {user?.fullname}
              </div>
              <div style={{ fontSize: 12, color: "#888" }}>
                {user?.jobtitle}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: "Period",
      dataIndex: "period",
      key: "period",
      render: (v) => <Text style={{ fontSize: 13, color: "#555" }}>{v}</Text>,
    },
    {
      title: "Service",
      dataIndex: "servicecount",
      key: "servicecount",
      align: "right",
      render: (v) => <ServiceCount $count={v}>{v}</ServiceCount>,
    },
  ];

  return (
    <>
      <Modal
        open={open}
        onCancel={handleClose}
        footer={null}
        centered
        width={660}
        title={null}
        styles={{
          body: { padding: "24px", maxHeight: "85vh", overflowY: "auto" },
        }}
      >
        <Spin spinning={loading}>
          {/* contentRef wraps everything — html2canvas captures this div */}
          <ModalBody ref={contentRef}>
            {/* ── Header ── */}
            <ModalHeader>
              <HeaderLeft>
                <TitleRow>
                  <AssetTitle>
                    {asset?.model || "Asset"}
                    {asset?.assetname ? ` – ${asset.assetname}` : ""}
                  </AssetTitle>
                  {asset?.asset_service_status === "In Service" && (
                    <InServiceTag>In Service</InServiceTag>
                  )}
                </TitleRow>
                <AssetSubtitle>Asset ID/Name: {asset?.assetname}</AssetSubtitle>
              </HeaderLeft>
              <HeaderRight>
                {!isPdfExporting && (
                  <div className="me-4 d-flex gap-2">
                    <Button
                      icon={<FilePdfOutlined />}
                      loading={exportLoading}
                      onClick={handleExportPDF}
                      style={{ borderRadius: 6 }}
                    >
                      Export PDF
                    </Button>

                    {asset?.assigned_to &&
                      Menulist?.find(
                        (item) =>
                          item?.key === "HRResources" &&
                          item?.subMenu?.find(
                            (subItem) =>
                              subItem?.key === "assets" &&
                              subItem?.permission?.delete
                          )
                      ) && (
                        <Button
                          loading={revokeLoading}
                          style={{ borderRadius: 6 }}
                          onClick={confirmRevokeAsset}
                        >
                          Revoke Asset
                        </Button>
                      )}
                    {Menulist?.find(
                      (item) =>
                        item?.key === "HRResources" &&
                        item?.subMenu?.find(
                          (subItem) =>
                            subItem?.key === "assets" &&
                            subItem?.permission?.edit
                        )
                    ) && (
                      <Button
                        type="primary"
                        style={{ background: "#2563eb", borderRadius: 6 }}
                        onClick={() => {
                          handleClose();
                          onEdit?.(asset);
                        }}
                      >
                        Edit Asset
                      </Button>
                    )}
                  </div>
                )}
              </HeaderRight>
            </ModalHeader>

            {/* ── Current Owner ── */}
            <Section>
              <SectionTitle>Current Owner</SectionTitle>
              {currentOwner ? (
                <OwnerCard>
                  <OwnerLeft>
                    {currentOwner.avatar ? (
                      <Avatar src={currentOwner.avatar} size={48} />
                    ) : (
                      <Avatar>
                        {startCase(currentOwner.name || "?").charAt(0)}
                      </Avatar>
                    )}
                    <OwnerInfo>
                      <OwnerName>{currentOwner.name}</OwnerName>
                      <OwnerMeta>
                        {currentOwner.role}
                        {currentOwner.department &&
                          ` • ${currentOwner.department}`}
                      </OwnerMeta>
                    </OwnerInfo>
                  </OwnerLeft>
                  <OwnerRight>
                    <AssignedLabel>Assigned On</AssignedLabel>
                    <AssignedDate>{currentOwner.assignedon}</AssignedDate>
                  </OwnerRight>
                </OwnerCard>
              ) : (
                <Text style={{ color: "#bbb" }}>Not assigned</Text>
              )}
            </Section>

            {/* ── Other Info ── */}
            <Section>
              <SectionTitle>Other Info</SectionTitle>
              <OtherInfoGrid>
                <InfoBlock>
                  <InfoLabel>Attachment</InfoLabel>
                  {asset?.filename ? (
                    <AttachmentLink
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePreviewClick(asset);
                      }}
                    >
                      {asset.filename}
                    </AttachmentLink>
                  ) : (
                    <InfoValue style={{ color: "#bbb" }}>---</InfoValue>
                  )}
                </InfoBlock>
                <InfoBlock>
                  <InfoLabel>Purchase Date</InfoLabel>
                  <InfoValue>{purchaseDate}</InfoValue>
                </InfoBlock>
                <InfoBlock>
                  <InfoLabel>Warranty End</InfoLabel>
                  <InfoValue>{warrantyEnd}</InfoValue>
                </InfoBlock>
              </OtherInfoGrid>
            </Section>

            {/* ── Historical Owners ── */}
            <Section>
              <HistoricalHeader>
                <SectionTitle style={{ marginBottom: 0 }}>
                  <HistoryOutlined style={{ marginRight: 6 }} />
                  Historical Owners
                </SectionTitle>
                <Text style={{ fontSize: 12, color: "#888" }}>
                  Total: {historicalOwners.length} Owners
                </Text>
              </HistoricalHeader>
              <Table
                columns={historyColumns}
                dataSource={historicalOwners}
                pagination={false}
                size="small"
                rowKey={(r) => `${r.ownername}-${r.key}`}
                style={{ borderRadius: 8 }}
              />
            </Section>

            {/* ── Performance Timeline ── */}
            <Section>
              <TimelineHeader>
                <TimelineSectionTitle>
                  Performance Timeline
                </TimelineSectionTitle>
                <Dropdown
                  open={filterOpen}
                  onOpenChange={setFilterOpen}
                  trigger={["click"]}
                  dropdownRender={() => (
                    <FilterMenu
                      selected={selectedFilters}
                      onChange={toggleFilter}
                      onReset={resetFilters}
                    />
                  )}
                >
                  <FilterBtn
                    icon={<FilterOutlined />}
                    style={{
                      borderRadius: 8,
                      background:
                        selectedFilters.length > 0 ? "#eff6ff" : "#fff",
                      borderColor:
                        selectedFilters.length > 0 ? "#2563eb" : "#d9d9d9",
                      color: selectedFilters.length > 0 ? "#2563eb" : "#555",
                    }}
                  >
                    Filter
                    {selectedFilters.length > 0 && (
                      <span
                        style={{
                          background: "#2563eb",
                          color: "#fff",
                          borderRadius: "50%",
                          fontSize: 11,
                          width: 18,
                          height: 18,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginLeft: 4,
                        }}
                      >
                        {selectedFilters.length}
                      </span>
                    )}
                  </FilterBtn>
                </Dropdown>
              </TimelineHeader>

              {timelineEvents.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    color: "#bbb",
                    padding: "24px 0",
                    fontSize: 13,
                  }}
                >
                  No timeline events found.
                </div>
              ) : (
                <TimelineList>
                  {timelineEvents.map((event, idx) => {
                    const cfg =
                      eventConfig[event.type] || eventConfig["Asset Added"];
                    const IconComponent = cfg.Icon;

                    return (
                      <TimelineItem key={idx}>
                        <TLIconWrap>
                          <IconComponent
                            style={{
                              width: 22,
                              height: 22,
                            }}
                          />
                          <TLLine className="tl-line" />
                        </TLIconWrap>

                        <TLContent>
                          <TLLeft>
                            <TLEventName>{event.type}</TLEventName>
                            <TLEventDesc>{event.desc}</TLEventDesc>
                          </TLLeft>
                          <TLDate>{formatDate(event.date)}</TLDate>
                        </TLContent>
                      </TimelineItem>
                    );
                  })}
                </TimelineList>
              )}
            </Section>
          </ModalBody>
        </Spin>
      </Modal>

      <ViewDocument
        open={preview}
        onCancel={() => {
          setPreview(false);
          setPreviewURL("");
        }}
        data={previewURL}
      />
    </>
  );
};

export default AssetDetailDrawer;
